import { ListItemIcon, ListItemText, MenuItem } from "@mui/material";
import { useState } from "react";

import BaseEntityInline, {
  BaseEntityInlineInheritableProps,
} from "@/common/components/Entity/components/EntityInline/BaseEntityInline";
import AppIcon from "@/common/components/Icons/AppIcon";
import { StringHelper } from "@/common/helpers/string";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useDisplayProps } from "@/common/hooks/useDisplayProps";
import { VehicleDtoAll, VehicleDtoAny } from "@/common/mapping/mappers/vehicle";
import { enumService } from "@/common/services/enum";
import { apiClient } from "@/core/api/ApiClient";

import MenuWithTrigger from "../../Menu/MenuWithTrigger";
import { EntityInlineActionIcon } from "../components/EntityInline/EntityInlineActionIcon";
import { EntityInlineActionIconButton } from "../components/EntityInline/EntityInlineActionIconButton";
import { EntityInlineActions } from "../components/EntityInline/EntityInlineActions";
import VehicleActualStateModal from "./VehicleActualState/VehicleActualStateModal";
import { VehicleViewModal } from "./View/VehicleViewModal";

const defaultDisplayProps = { placeholder: true };
type DisplayProps = typeof defaultDisplayProps;

export const getVehicleInlineTitle = (data: Nil<VehicleDtoAll>, displayProps?: DisplayProps) =>
  data
    ? StringHelper.join(
        [
          data.plateNo?.plateNo || data?.identificationNumber,
          data.spec?.make?.name,
          data.spec?.model?.name,
          data.spec?.generation?.name,
          data.spec?.modification?.name,
          data.spec?.manufactureYear?.toString(),
          // "Vehicle",
          enumService.getEnumValueName("VehicleBodyType", data.spec?.bodyType),
          enumService.getEnumValueName("VehicleFuelType", data.spec?.fuelType),
        ],
        ", ",
        { skipEmpty: true },
      )
    : undefined;

export const getVehicleInlineTitleShort = (data: Nil<VehicleDtoAll>) =>
  data
    ? StringHelper.join(
        [
          data.plateNo?.plateNo || data?.identificationNumber,
          data.spec?.make?.name,
          data.spec?.model?.name,
        ],
        ", ",
        { skipEmpty: true },
      )
    : undefined;

export interface VehicleInlineProps extends BaseEntityInlineInheritableProps {
  entity: VehicleDtoAny | null | undefined;
  entityId?: string | null;
  withActions?: boolean;
  displayProps?: Partial<DisplayProps>;
}

/** Inline brief information about the entity. */
export default function VehicleInline({
  entity,
  entityId,
  displayProps,
  fetch = true,
  withActions,
  withMenu,
  viewProps,
  ...otherProps
}: VehicleInlineProps) {
  const displayPropsComputed = useDisplayProps([defaultDisplayProps, displayProps]);

  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isActualStateViewModalOpen, setIsActualStateViewModalOpen] = useState(false);

  const request = useApiRequest(
    apiClient.vehiclesApi.apiV1VehiclesVehicleIdGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      vehicleId: entityId!,
    },
    {
      deps: [entityId],
      skip: !fetch || !entityId || !!entity,
    },
  );
  entity = request?.data || entity;

  const entityIdComputed = entity?.id || entityId;

  return (
    <>
      <BaseEntityInline
        entity={entity}
        entityId={entityId}
        request={fetch ? request : undefined}
        icon={<AppIcon of='vehicle' inText />}
        content={getVehicleInlineTitle(entity as VehicleDtoAll, displayPropsComputed) || "Vehicle"}
        withActions={withActions}
        withMenu={withMenu}
        actions={
          !withActions ? undefined : (
            <EntityInlineActions>
              <EntityInlineActionIconButton
                onClick={() => setIsViewModalOpen(true)}
                tooltipProps={{ title: "Quick view" }}
              >
                <EntityInlineActionIcon of='quickView' />
              </EntityInlineActionIconButton>
            </EntityInlineActions>
          )
        }
        menu={
          !withMenu ? undefined : (
            <MenuWithTrigger
              withAuthCloseOnClick
              trigger={
                <EntityInlineActionIconButton>
                  <EntityInlineActionIcon of='moreVert' />
                </EntityInlineActionIconButton>
              }
            >
              <MenuItem dense onClick={() => setIsViewModalOpen(true)}>
                <ListItemIcon>
                  <AppIcon of='quickView' fontSize='small' />
                </ListItemIcon>
                <ListItemText>Quick view</ListItemText>
              </MenuItem>
              <MenuItem dense onClick={() => setIsActualStateViewModalOpen(true)}>
                <ListItemIcon>
                  <AppIcon of='quickView' fontSize='small' />
                </ListItemIcon>
                <ListItemText>Quick view actual state</ListItemText>
              </MenuItem>
            </MenuWithTrigger>
          )
        }
        {...otherProps}
        tooltipTitle={otherProps?.tooltipTitle || "Vehicle"}
      />

      {(withActions || withMenu) && (
        <>
          {/* View modal */}
          {entityIdComputed && (
            <VehicleViewModal
              open={isViewModalOpen}
              onClose={() => setIsViewModalOpen(false)}
              viewProps={{
                vehicleId: entityIdComputed,
                vehicle: entity,
                onUpdate: viewProps?.onUpdate || null,
                onDelete: viewProps?.onDelete || null,
              }}
            />
          )}

          {/* Actual state modal */}
          {entityIdComputed && (
            <VehicleActualStateModal
              open={isActualStateViewModalOpen}
              onClose={() => setIsActualStateViewModalOpen(false)}
              subProps={{
                vehicleId: entityIdComputed,
                vehicle: entity,
              }}
            />
          )}
        </>
      )}
    </>
  );
}
